import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { useCurrency } from '../context/CurrencyContext';
import Price from '../components/Price';
import sumInCurrency from '../utils/sumInCurrency';
import { formatMoney } from '../utils/fx';
import { useTranslation } from 'react-i18next';

const EMPTY_SHIP = {
  name: '', phone: '', address: '', city: '', state: '', country: '', zip: '', notes: '',
};

export default function Cart() {
  const navigate = useNavigate();
  const { user, createOrder, listPayableSellers, createMarketplaceCheckout } = useAuth();
  const { items, setQty, removeItem, clear } = useCart();
  const { displayCurrency, rates } = useCurrency();
  const { i18n } = useTranslation();
  // Total convertido a la divisa preferida del usuario. Cada item conserva
  // su divisa canónica; aquí solo se suma para presentación.
  const total = sumInCurrency(
    items.map((it) => ({ amount: Number(it.price) * (it.qty || 1), currency: it.currency || 'USD' })),
    displayCurrency,
    rates,
  );
  const totalLabel = formatMoney(total, displayCurrency, i18n.language);
  const [shipping, setShipping] = useState(() => ({
    ...EMPTY_SHIP,
    name: user?.name || '',
    country: user?.location ? user.location.split(',').pop().trim() : '',
  }));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [done, setDone] = useState([]);

  const set = (k) => (e) => setShipping((s) => ({ ...s, [k]: e.target.value }));

  // Un cobro con tarjeta tiene un solo destino (la cuenta del vendedor) y una
  // sola divisa, así que el carrito se paga por grupos vendedor + divisa.
  const groups = useMemo(() => {
    const map = new Map();
    for (const it of items) {
      const key = `${it.sellerId}|${it.currency || 'USD'}`;
      if (!map.has(key)) map.set(key, { key, sellerId: it.sellerId, sellerName: it.sellerName, currency: it.currency || 'USD', items: [] });
      map.get(key).items.push(it);
    }
    return [...map.values()];
  }, [items]);

  // Vendedores del carrito que ya activaron cobros con tarjeta (Stripe Connect).
  const [payable, setPayable] = useState(() => new Set());
  const sellerIdsKey = [...new Set(items.map((it) => it.sellerId).filter(Boolean))].sort().join(',');
  useEffect(() => {
    if (!sellerIdsKey) return;
    listPayableSellers(sellerIdsKey.split(','))
      .then((ids) => setPayable(new Set(ids)))
      .catch(() => setPayable(new Set()));
  }, [sellerIdsKey, listPayableSellers]);

  const shippingOk = () => {
    if (!shipping.name.trim() || !shipping.address.trim() || !shipping.city.trim() || !shipping.country.trim()) {
      setErr('Completa nombre, dirección, ciudad y país'); return false;
    }
    return true;
  };

  const checkout = async (group) => {
    setErr('');
    if (!group || group.items.length === 0) { setErr('Tu carrito está vacío'); return; }
    if (!shippingOk()) return;
    setBusy(group.key);
    try {
      if (payable.has(group.sellerId)) {
        // Pago con tarjeta en Stripe Checkout. El carrito se limpia en la
        // página de éxito; si el comprador cancela, vuelve aquí intacto.
        const { url } = await createMarketplaceCheckout({
          items: group.items.map((it) => ({ publicationId: it.publicationId, units: it.qty })),
          shipping,
        });
        window.location.href = url;
        return;
      }
      // Vendedor sin cobros activos: flujo original, se coordina el pago.
      const created = [];
      for (const it of group.items) {
        created.push(await createOrder({ publicationId: it.publicationId, units: it.qty, shipping }));
        removeItem(it.publicationId);
      }
      setDone((d) => [...d, ...created]);
      setBusy(false);
    } catch (e) {
      setErr(e.response?.data?.message || 'Error procesando la compra');
      setBusy(false);
    }
  };

  if (done.length > 0 && items.length === 0) {
    return (
      <div className="profile">
        <Helmet><title>Compra confirmada — Troly</title></Helmet>
        <div className="profile-body container" style={{ maxWidth: 720 }}>
          <h1 style={{ fontSize: 28, marginBottom: 12 }}>¡Gracias por tu compra!</h1>
          <p style={{ color: 'var(--muted)', marginBottom: 24 }}>
            Generamos {done.length} {done.length === 1 ? 'orden' : 'órdenes'}. Los vendedores serán notificados para coordinar el envío.
          </p>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 10 }}>
            {done.map((o) => (
              <li key={o._id} style={{ padding: 14, border: '1px solid var(--line)', borderRadius: 10, background: 'var(--surface)' }}>
                <div style={{ fontSize: 12, color: 'var(--muted)', fontFamily: 'monospace' }}>#{o._id}</div>
                <div style={{ fontWeight: 600, marginTop: 4 }}>
                  {o.units} u. · <Price amount={Number(o.total)} currency={o.currency || 'USD'} />
                </div>
                <div style={{ fontSize: 12, color: 'var(--muted)' }}>{o.shipping?.address}, {o.shipping?.city}, {o.shipping?.country}</div>
              </li>
            ))}
          </ul>
          <div style={{ marginTop: 24, display: 'flex', gap: 8 }}>
            <Link to="/profile" className="btn-teal">Volver a mi perfil</Link>
            <Link to="/" className="btn-ghost">Seguir explorando</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="profile">
      <Helmet><title>Carrito — Troly</title></Helmet>
      <div className="profile-body container" style={{ maxWidth: 980 }}>
        <h1 style={{ fontSize: 28, marginBottom: 6 }}>Carrito</h1>
        <p style={{ color: 'var(--muted)', marginBottom: 24 }}>
          {items.length === 0 ? 'Tu carrito está vacío.' : `${items.length} ${items.length === 1 ? 'pieza' : 'piezas'} de vendedores Troly.`}
        </p>

        {err && <div className="error-banner" style={{ marginBottom: 14 }}>{err}</div>}

        {items.length === 0 ? (
          <Link to="/" className="btn-teal">Explorar piezas</Link>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 24 }}>
            <div>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 12 }}>
                {items.map((it) => (
                  <li key={it.publicationId} style={{ display: 'flex', gap: 12, padding: 12, border: '1px solid var(--line)', borderRadius: 10, background: 'var(--surface)' }}>
                    <div style={{ width: 80, height: 80, background: 'var(--bg)', borderRadius: 8, overflow: 'hidden', flex: '0 0 auto' }}>
                      {it.image ? <img src={it.image} alt={it.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : null}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600 }}>{it.name}</div>
                      <div style={{ fontSize: 12, color: 'var(--muted)' }}>Vendedor: {it.sellerName}</div>
                      <div style={{ fontSize: 13, marginTop: 4 }}>
                        <Price amount={Number(it.price)} currency={it.currency || 'USD'} suffix=" c/u" />
                      </div>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8 }}>
                        <label style={{ fontSize: 12, color: 'var(--muted)' }}>Cantidad:</label>
                        <input
                          type="number"
                          min="1"
                          max={it.units}
                          value={it.qty}
                          onChange={(e) => setQty(it.publicationId, e.target.value)}
                          className="light-input"
                          style={{ width: 64 }}
                        />
                        <span style={{ fontSize: 11, color: 'var(--muted)' }}>(disp. {it.units})</span>
                        <button type="button" className="btn-ghost btn-sm" style={{ marginLeft: 'auto', color: '#ef4444' }} onClick={() => removeItem(it.publicationId)}>Quitar</button>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
              <div style={{ marginTop: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <button type="button" className="btn-ghost" onClick={clear}>Vaciar carrito</button>
                <div style={{ fontSize: 18, fontWeight: 700 }}>Total: {totalLabel}</div>
              </div>
            </div>

            <aside style={{ padding: 16, border: '1px solid var(--line)', borderRadius: 12, background: 'var(--surface)', height: 'fit-content' }}>
              <h3 style={{ marginTop: 0, marginBottom: 12 }}>Información de envío</h3>
              <div style={{ display: 'grid', gap: 10 }}>
                <input className="light-input" placeholder="Nombre completo" value={shipping.name} onChange={set('name')} />
                <input className="light-input" placeholder="Teléfono" value={shipping.phone} onChange={set('phone')} />
                <input className="light-input" placeholder="Dirección (calle, número, colonia)" value={shipping.address} onChange={set('address')} />
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <input className="light-input" placeholder="Ciudad" value={shipping.city} onChange={set('city')} />
                  <input className="light-input" placeholder="Estado / Provincia" value={shipping.state} onChange={set('state')} />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <input className="light-input" placeholder="País" value={shipping.country} onChange={set('country')} />
                  <input className="light-input" placeholder="Código postal" value={shipping.zip} onChange={set('zip')} />
                </div>
                <textarea className="light-input" style={{ minHeight: 60 }} placeholder="Notas para el vendedor (opcional)" value={shipping.notes} onChange={set('notes')} maxLength={400} />
              </div>
              {groups.map((g) => {
                const card = payable.has(g.sellerId);
                const sub = g.items.reduce((sum, it) => sum + Number(it.price) * (it.qty || 1), 0);
                const subLabel = formatMoney(sub, g.currency, i18n.language);
                return (
                  <div key={g.key} style={{ marginTop: 14 }}>
                    {groups.length > 1 && (
                      <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 6 }}>
                        {g.sellerName} · {g.items.length} {g.items.length === 1 ? 'pieza' : 'piezas'}
                      </div>
                    )}
                    <button
                      type="button"
                      className="btn-teal"
                      style={{ width: '100%', minHeight: 44 }}
                      onClick={() => checkout(g)}
                      disabled={!!busy}
                    >
                      {busy === g.key ? 'Procesando…' : card ? `Pagar con tarjeta · ${subLabel}` : `Confirmar compra · ${subLabel}`}
                    </button>
                    <p style={{ fontSize: 11, color: 'var(--muted)', marginTop: 6, marginBottom: 0 }}>
                      {card
                        ? 'Pago seguro con Stripe. Tus piezas quedan apartadas 30 minutos mientras pagas.'
                        : <>Este vendedor aún no cobra con tarjeta: la orden queda <strong>pending</strong> y coordinas el pago con él.</>}
                    </p>
                  </div>
                );
              })}
            </aside>
          </div>
        )}
      </div>
    </div>
  );
}
